import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowRight, X, Plus, Boat, Trash, Upload, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_SHIPMENTS = 15;

// Local date for "today" formatted as YYYY-MM-DD (the HTML5 date input floor).
const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function CreateFreight() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    deadline: "",
    currency: "USD",
    validity_note: "",
  });
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [shipments, setShipments] = useState([{ shipment_ref: "", cargo_ready_date: "" }]);
  const [pdf, setPdf] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdf(data);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("Invalid email");
      return;
    }
    if (emails.includes(e)) return;
    setEmails([...emails, e]);
    setEmailInput("");
  };

  const addShipment = () => {
    if (shipments.length >= MAX_SHIPMENTS) {
      toast.error(`Maximum ${MAX_SHIPMENTS} shipments per auction`);
      return;
    }
    setShipments([...shipments, { shipment_ref: "", cargo_ready_date: "" }]);
  };

  const updateShipment = (idx, patch) => {
    setShipments(shipments.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeShipment = (idx) => {
    if (shipments.length === 1) {
      toast.error("At least one shipment is required");
      return;
    }
    setShipments(shipments.filter((_, i) => i !== idx));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (emails.length === 0) { setError("Add at least one bidder email."); return; }
    if (shipments.length === 0) { setError("Add at least one shipment."); return; }
    const refs = new Set();
    for (const s of shipments) {
      const ref = s.shipment_ref.trim();
      if (!ref) { setError("Every shipment needs a reference."); return; }
      if (refs.has(ref)) { setError(`Duplicate shipment reference: ${ref}`); return; }
      refs.add(ref);
      if (!s.cargo_ready_date) { setError(`Shipment ${ref} needs a container-pickup date.`); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        type: "freight",
        title: form.title,
        description: form.description,
        deadline: form.deadline || null,
        requirement_path: pdf?.storage_path || null,
        requirement_filename: pdf?.original_filename || null,
        invited_emails: emails,
        currency: form.currency,
        validity_note: form.validity_note.trim(),
        shipments: shipments.map((s) => ({
          shipment_ref: s.shipment_ref.trim(),
          cargo_ready_date: s.cargo_ready_date,
        })),
      };
      const { data } = await api.post("/auctions", payload);
      toast.success("Freight auction created");
      navigate(`/auctions/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="create-freight-page">
      <TopBar />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 flex items-center gap-2">
          <Boat size={12} weight="bold" /> New Freight Auction
        </div>
        <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tightest leading-none mb-3">
          Auction container shipments
        </h1>
        <p className="text-sm text-neutral-600 mb-10 max-w-2xl">
          Each shipment = 1 container. Bidders quote a rate <strong>and</strong> an assured vessel + ETD
          per shipment. Bidders may cherry-pick which shipments they can lift. Up to {MAX_SHIPMENTS} shipments.
        </p>

        <form onSubmit={onSubmit} className="space-y-10">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Field label="Title" required>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  placeholder="e.g. Feb Freight — 8 containers to EU"
                  className="rounded-none border-black/20 h-11"
                  data-testid="freight-title-input"
                />
              </Field>
              <Field label="Description" required>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                  rows={5}
                  placeholder="Scope, origin/destination, container type, incoterms..."
                  className="rounded-none border-black/20"
                  data-testid="freight-desc-input"
                />
              </Field>
              <Field label="Validity note (shown to bidders)">
                <Input
                  value={form.validity_note}
                  onChange={(e) => setForm({ ...form, validity_note: e.target.value })}
                  placeholder="e.g. Containers moving in the next 30 days"
                  className="rounded-none border-black/20 h-11"
                  data-testid="freight-validity-input"
                />
              </Field>
              <Field label="Bid deadline (optional)">
                <Input
                  type="datetime-local"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="rounded-none border-black/20 h-11"
                  data-testid="freight-deadline-input"
                />
              </Field>
              <Field label="Currency">
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="rounded-none border-black/20 h-11" data-testid="freight-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="USD">USD — US Dollar ($)</SelectItem>
                    <SelectItem value="INR">INR — Indian Rupee (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="space-y-6">
              <Field label="Invite Bidders (by email)" required>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                    placeholder="carrier@shipping.com"
                    className="rounded-none border-black/20 h-11"
                    data-testid="freight-invite-email-input"
                  />
                  <Button type="button" onClick={addEmail} className="rounded-none bg-[#0B1929] text-white h-11 px-4" data-testid="freight-invite-email-add">
                    Add
                  </Button>
                </div>
                {emails.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2" data-testid="freight-invited-list">
                    {emails.map((e) => (
                      <span key={e} className="inline-flex items-center gap-2 border border-black/20 px-2.5 py-1 text-xs font-mono-plex">
                        {e}
                        <button type="button" onClick={() => setEmails(emails.filter((x) => x !== e))}>
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </Field>
              <div className="border border-black/10 bg-[#F8F9FA] p-4 text-xs text-neutral-600 leading-relaxed">
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold mb-1.5">How this works</div>
                Bidders see all shipments and choose which ones to quote. Each quote must include a vessel name and
                a committed ETD on or after that shipment&apos;s container-pickup date. At close, we award each container
                to the cheapest eligible bidder — you can override any allocation before committing.
              </div>

              <Field label="Requirement Attachment (optional)">
                {!pdf ? (
                  <label className="border border-dashed border-black/20 p-3 flex items-center gap-3 cursor-pointer hover:border-black" data-testid="freight-req-upload-zone">
                    <Upload size={16} weight="bold" />
                    <span className="text-sm">
                      {uploading ? "Uploading..." : "Click to upload (RFQ PDF, shipping schedule, port list…)"}
                    </span>
                    <input type="file" hidden onChange={onUpload} disabled={uploading} data-testid="freight-req-upload-input" />
                  </label>
                ) : (
                  <div className="border border-black/15 p-3 flex items-center gap-3" data-testid="freight-req-attachment">
                    <FileText size={16} weight="bold" />
                    <div className="flex-1 text-sm truncate font-mono-plex">{pdf.original_filename}</div>
                    <button type="button" onClick={() => setPdf(null)} data-testid="freight-req-attachment-remove">
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* SHIPMENTS */}
          <div>
            <div className="border-b border-black pb-3 mb-4 flex items-baseline justify-between">
              <div>
                <h2 className="font-display font-extrabold text-2xl tracking-tightest">Shipments</h2>
                <p className="text-xs text-neutral-500 mt-1">One row per container. Container-pickup date is the earliest ETD you&apos;ll accept for that box.</p>
              </div>
              <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{shipments.length} / {MAX_SHIPMENTS}</span>
            </div>

            <div className="border border-black/10" data-testid="shipments-table">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FA] border-b border-black/10">
                  <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                    <th className="text-left p-3 w-12">#</th>
                    <th className="text-left p-3">Shipment Reference *</th>
                    <th className="text-left p-3 w-56">Container Pickup Date *</th>
                    <th className="text-right p-3 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s, i) => (
                    <tr key={i} className="border-b border-black/5" data-testid={`shipment-row-${i}`}>
                      <td className="p-3 font-mono-plex text-neutral-500">{i + 1}</td>
                      <td className="p-3">
                        <Input
                          value={s.shipment_ref}
                          onChange={(e) => updateShipment(i, { shipment_ref: e.target.value })}
                          placeholder={`e.g. S${i + 1} or PO-1234`}
                          className="rounded-none border-black/20 h-9 font-mono-plex"
                          data-testid={`shipment-ref-input-${i}`}
                          required
                        />
                      </td>
                      <td className="p-3">
                        <Input
                          type="date"
                          min={todayIso()}
                          value={s.cargo_ready_date}
                          onChange={(e) => updateShipment(i, { cargo_ready_date: e.target.value })}
                          className="rounded-none border-black/20 h-9 font-mono-plex"
                          data-testid={`shipment-crd-input-${i}`}
                          required
                        />
                      </td>
                      <td className="p-3 text-right">
                        <button
                          type="button"
                          onClick={() => removeShipment(i)}
                          className="text-neutral-500 hover:text-red-600 disabled:opacity-30"
                          data-testid={`remove-shipment-${i}`}
                          disabled={shipments.length === 1}
                          title={shipments.length === 1 ? "At least one shipment required" : "Remove"}
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-3 border-t border-black/10 bg-white">
                <button
                  type="button"
                  onClick={addShipment}
                  disabled={shipments.length >= MAX_SHIPMENTS}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold underline underline-offset-4 disabled:opacity-40 disabled:no-underline"
                  data-testid="add-shipment-btn"
                >
                  <Plus size={12} weight="bold" /> Add shipment
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3" data-testid="create-freight-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full lg:w-auto lg:px-12 rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp"
            data-testid="create-freight-submit"
          >
            {submitting ? "Creating..." : "Launch Freight Auction"}
            <ArrowRight size={14} weight="bold" className="ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">
      {label}{required && <span className="text-red-600 ml-1">*</span>}
    </Label>
    {children}
  </div>
);
